import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

import { Item, Member, Status } from '@/features/backlog/types';
import { PRIORITIES, TASK_TYPES, priorityMeta, taskTypeMeta, hiddenFromBoard } from '@/features/backlog/constants';
import { closedLabel, isClosedKind, statusKind } from '@/components/status-dot';
import { taskPath } from '@/constants/routes';
import { CanvasElement } from '@/features/canvas/types';
import { TaskTypeGlyph, PriorityChevron, Chevron, ExternalLink } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import EpicChip from '@/components/epic-chip';
import { Popover } from '@/components/ui/Popover';
import { cn } from '@/components/ui/cn';
import { useCanvasItems } from './use-canvas-items';
import { fieldOn } from './card-fields';

// Interactive sub-controls must not start a canvas move/marquee — swallow the pointerdown.
const stop = (e: ReactPointerEvent) => e.stopPropagation();

const statusMeta = (item: Item, statuses: Status[] | undefined) => {
  const s = statuses?.find((x) => x.value === item.status);
  return { label: s?.label ?? item.status.replace(/[_-]+/g, ' '), color: s?.color ?? '#a1a1aa' };
};

/** A small circle in the status colour with the workflow glyph, opening a per-board status picker. */
function StatusPicker({ item, statuses, onPick }: { item: Item; statuses: Status[]; onPick: (v: string) => void }) {
  const m = statusMeta(item, statuses);
  return (
    <Popover
      portal
      trigger={({ toggle }) => (
        <button type="button" onPointerDown={stop} onClick={toggle} title={m.label} className="flex items-center gap-1 rounded px-0.5 py-0.5 hover:bg-layer">
          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: m.color }} />
          <span className="max-w-[90px] truncate text-[11px] text-fg-soft">{m.label}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="min-w-[160px] p-1">
          {statuses.map((s) => (
            <button
              key={s.value}
              type="button"
              onPointerDown={stop}
              onClick={() => { onPick(s.value); close(); }}
              className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-layer', s.value === item.status && 'bg-layer')}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="truncate text-fg-soft">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

function AssigneePicker({ item, members, onPick }: { item: Item; members: Member[]; onPick: (id: string | null) => void }) {
  const active = members.filter((m) => m.active !== false).sort((a, b) => a.name.localeCompare(b.name));
  const mem = item.assignee_id ? members.find((m) => m.id === item.assignee_id) : undefined;
  return (
    <Popover
      portal
      trigger={({ toggle }) => (
        <button type="button" onPointerDown={stop} onClick={toggle} title={mem?.name ?? 'Unassigned'} className="rounded-full hover:opacity-80">
          <MemberAvatar member={mem} size={20} />
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-[240px] min-w-[180px] overflow-y-auto p-1">
          <button type="button" onPointerDown={stop} onClick={() => { onPick(null); close(); }} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-fg-faint hover:bg-layer">
            <MemberAvatar member={undefined} size={18} /> Unassigned
          </button>
          {active.map((m) => (
            <button key={m.id} type="button" onPointerDown={stop} onClick={() => { onPick(m.id); close(); }} className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-layer', m.id === item.assignee_id && 'bg-layer')}>
              <MemberAvatar member={m} size={18} /> <span className="truncate text-fg-soft">{m.name}</span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

function PriorityPicker({ item, onPick }: { item: Item; onPick: (v: string) => void }) {
  const pm = priorityMeta(item.priority);
  return (
    <Popover
      portal
      trigger={({ toggle }) => (
        <button type="button" onPointerDown={stop} onClick={toggle} title={pm.label} className="flex h-5 w-5 items-center justify-center rounded hover:bg-layer">
          <PriorityChevron level={pm.level} color={pm.color} />
        </button>
      )}
    >
      {(close) => (
        <div className="min-w-[140px] p-1">
          {PRIORITIES.map((p) => (
            <button key={p.value} type="button" onPointerDown={stop} onClick={() => { onPick(p.value); close(); }} className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-layer', p.value === item.priority && 'bg-layer')}>
              <span className="flex h-4 w-4 items-center justify-center"><PriorityChevron level={p.level} color={p.color} /></span>
              <span className="text-fg-soft">{p.label}</span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

function TypePicker({ item, onPick }: { item: Item; onPick: (v: string) => void }) {
  const tm = taskTypeMeta(item.type);
  return (
    <Popover
      portal
      trigger={({ toggle }) => (
        <button type="button" onPointerDown={stop} onClick={toggle} title={tm.label} className="flex h-4 w-4 items-center justify-center">
          <TaskTypeGlyph type={item.type} color={tm.color} />
        </button>
      )}
    >
      {(close) => (
        <div className="min-w-[140px] p-1">
          {TASK_TYPES.map((t) => (
            <button key={t.value} type="button" onPointerDown={stop} onClick={() => { onPick(t.value); close(); }} className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-layer', t.value === item.type && 'bg-layer')}>
              <TaskTypeGlyph type={t.value} color={t.color} /> <span className="text-fg-soft">{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

/** A compact child/member row rendered inside a card (the subtask tree / epic member list). Honours the
 *  card's field config (`on`) so children show the same data columns the user chose for the card. */
function MiniRow({ item, statuses, depth, onOpen, childrenBy, on, members }: { item: Item; statuses: Status[] | undefined; depth: number; onOpen: (it: Item) => void; childrenBy: Map<string, Item[]>; on: (k: string) => boolean; members: Member[] }) {
  const [open, setOpen] = useState(false);
  const kids = (childrenBy.get(item.id) ?? []).filter((c) => !hiddenFromBoard(c));
  const m = statusMeta(item, statuses);
  const tm = taskTypeMeta(item.type);
  const pm = priorityMeta(item.priority);
  const labels = item.labels ?? [];
  const assignee = item.assignee_id ? members.find((x) => x.id === item.assignee_id) : undefined;
  return (
    <>
      <div className="flex items-center gap-2 rounded-md py-1 pr-1 transition-colors hover:bg-layer" style={{ paddingLeft: depth * 15 + 4 }}>
        {kids.length ? (
          <button type="button" onPointerDown={stop} onClick={() => setOpen((v) => !v)} className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-fg-faint hover:text-fg">
            <Chevron className={cn('h-3 w-3 transition-transform', open ? 'rotate-90' : '')} />
          </button>
        ) : (
          <span className="h-3.5 w-3.5 shrink-0" />
        )}
        {on('type') ? <TaskTypeGlyph type={item.type} color={tm.color} /> : null}
        <span className="item-key shrink-0 rounded px-1 py-px tnum text-[9px] font-semibold">{item.key}</span>
        <button type="button" onPointerDown={stop} onClick={() => onOpen(item)} className="min-w-0 flex-1 truncate text-left text-[11px] text-fg-soft hover:text-fg" title={item.title}>
          {item.title || 'Untitled'}
        </button>
        {on('labels') && labels.length > 0 ? (
          <span className="hidden shrink-0 items-center gap-0.5 sm:flex" title={labels.join(', ')}>
            {labels.slice(0, 1).map((l) => <span key={l} className="rounded-full bg-layer px-1 py-px text-[8.5px] font-medium text-fg-faint">{l}</span>)}
          </span>
        ) : null}
        {on('priority') ? <span className="flex h-3 w-3 shrink-0 items-center justify-center" title={pm.label}><PriorityChevron level={pm.level} color={pm.color} /></span> : null}
        {on('status') ? <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: m.color }} title={m.label} /> : null}
        {on('assignee') ? <MemberAvatar member={assignee} size={14} className="shrink-0" /> : null}
      </div>
      {open ? kids.map((c) => <MiniRow key={c.id} item={c} statuses={statuses} depth={depth + 1} onOpen={onOpen} childrenBy={childrenBy} on={on} members={members} />) : null}
    </>
  );
}

/** The whiteboard board-item card (SBB-13). Renders a live backlog item as a kanban-style card, with a
 *  subtask tree, epic-member list (when the referenced item is an epic), inline editing (title / status /
 *  assignee / priority / type), and an open-in-new-tab affordance. All state stays live via realtime. */
export default function ItemCard({ element, onAutoHeight }: { element: CanvasElement; onAutoHeight?: (px: number) => void }) {
  const { itemsById, childrenBy, membersByEpic, statusesByBoard, members, boardsById, saveItem } = useCanvasItems();
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Report the card's natural height so the element box grows to fit (no clip / scrollbar).
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !onAutoHeight) return undefined;
    const ro = new ResizeObserver(() => onAutoHeight(node.offsetHeight));
    ro.observe(node);
    onAutoHeight(node.offsetHeight);
    return () => ro.disconnect();
  }, [onAutoHeight]);

  const id = element.style?.item_id;
  const item = id ? itemsById.get(id) : undefined;

  if (!item) {
    return (
      <div ref={rootRef} className="flex w-full items-center justify-center rounded-card border border-line bg-surface-raised px-3 py-8 text-center text-[11px] text-fg-faint shadow">
        {id ? 'Loading item…' : 'No item linked'}
      </div>
    );
  }

  const board = boardsById.get(item.board_id);
  const statuses = statusesByBoard.get(item.board_id);
  const isEpic = item.type === 'epic' || item.type === 'initiative';
  const epic = item.epic_id ? itemsById.get(item.epic_id) : undefined;
  const openInNewTab = (it: Item) => {
    const b = boardsById.get(it.board_id);
    if (b) window.open(taskPath(b.slug, it.key), '_blank', 'noopener,noreferrer');
  };

  // Epic member list (by epic_id) or the item's own subtask tree (by parent_id). A member whose parent is
  // ALSO a member is rendered NESTED under that parent (MiniRow expands childrenBy), so it must NOT also
  // appear at the top level — otherwise it shows up twice (as a root AND as a child). The done/total count
  // still reflects EVERY member (each is shown exactly once, just at the right depth).
  const allEpicMembers = isEpic ? (membersByEpic.get(item.id) ?? []).filter((c) => !hiddenFromBoard(c)) : [];
  const epicMemberIds = new Set(allEpicMembers.map((m) => m.id));
  const epicMembers = allEpicMembers.filter((m) => !m.parent_id || !epicMemberIds.has(m.parent_id));
  const topKids = (childrenBy.get(item.id) ?? []).filter((c) => !hiddenFromBoard(c));
  // Closed (done or dropped) counts as finished; the label names the dropped ones (closedLabel).
  const memberKinds = allEpicMembers.map((c) => statusKind(statusMeta(c, statusesByBoard.get(c.board_id)).label));
  const closedCount = memberKinds.filter(isClosedKind).length;
  const droppedCount = memberKinds.filter((k) => k === 'dropped').length;

  const tm = taskTypeMeta(item.type);
  const on = (k: string) => fieldOn(element.style, k);
  const labels = item.labels ?? [];
  const showFooter = on('status') || on('priority') || on('assignee');
  return (
    <div ref={rootRef} className="flex w-full flex-col rounded-card border border-line bg-surface-raised shadow" style={isEpic ? { boxShadow: `inset 3px 0 0 ${tm.color}` } : undefined}>
      {/* header */}
      <div className="shrink-0 px-2.5 pt-2">
        {epic && on('epic') ? <EpicChip epic={epic} className="mb-1" /> : null}
        <div className="flex items-start gap-1.5">
          {on('type') ? <span className="mt-0.5 shrink-0"><TypePicker item={item} onPick={(v) => saveItem(item.id, { type: v as Item['type'] })} /></span> : null}
          <span className="item-key mt-px shrink-0 rounded px-1 py-px tnum text-[10px] font-semibold">{item.key}</span>
          {board && on('board') ? <span className="mt-px shrink-0 truncate rounded-full bg-layer px-1.5 text-[9px] font-medium text-fg-faint" title={board.name}>{board.name}</span> : null}
          <button type="button" onPointerDown={stop} onClick={() => openInNewTab(item)} title="Open item" className="ml-auto shrink-0 text-fg-ghost transition-colors hover:text-fg">
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </div>
        {titleDraft !== null ? (
          <input
            autoFocus
            value={titleDraft}
            onPointerDown={stop}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={() => { const t = titleDraft.trim(); if (t && t !== item.title) saveItem(item.id, { title: t }); setTitleDraft(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setTitleDraft(null); }}
            className="mt-1 w-full rounded border border-accent/40 bg-surface-inset px-1 py-0.5 text-[13px] font-medium text-fg focus:outline-none"
          />
        ) : (
          <button type="button" onPointerDown={stop} onDoubleClick={() => setTitleDraft(item.title)} onClick={() => setTitleDraft(item.title)} className="mt-1 block w-full text-left text-[13px] font-medium leading-snug text-fg" title="Click to edit">
            {item.title || <span className="text-fg-ghost">Untitled</span>}
          </button>
        )}
      </div>

      {/* labels */}
      {on('labels') && labels.length > 0 ? (
        <div className="mt-1.5 flex shrink-0 flex-wrap gap-1 px-2.5">
          {labels.map((l) => (
            <span key={l} className="rounded-full bg-layer px-1.5 py-px text-[9.5px] font-medium text-fg-faint">{l}</span>
          ))}
        </div>
      ) : null}

      {/* footer controls */}
      {showFooter ? (
        <div className="mt-1.5 flex shrink-0 items-center gap-2 px-2.5 pb-1.5">
          {on('status') && statuses ? <StatusPicker item={item} statuses={statuses} onPick={(v) => saveItem(item.id, { status: v })} /> : null}
          <span className="ml-auto flex items-center gap-1.5">
            {on('priority') ? <PriorityPicker item={item} onPick={(v) => saveItem(item.id, { priority: v as Item['priority'] })} /> : null}
            {on('assignee') ? <AssigneePicker item={item} members={members} onPick={(mid) => saveItem(item.id, { assignee_id: mid })} /> : null}
          </span>
        </div>
      ) : (
        <div className="pb-1.5" />
      )}

      {/* body: subtree or epic members */}
      {!on('children') ? null : isEpic ? (
        <div className="border-t border-line px-2.5 py-1.5">
          <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
            <span>Epic items</span>
            <span className="ml-2 min-w-0 truncate tnum">{closedLabel(closedCount, droppedCount, allEpicMembers.length)}</span>
          </div>
          {allEpicMembers.length === 0 ? (
            <div className="py-2 text-center text-[11px] text-fg-faint">No items linked to this epic.</div>
          ) : (
            epicMembers.map((c) => <MiniRow key={c.id} item={c} statuses={statusesByBoard.get(c.board_id)} depth={0} onOpen={openInNewTab} childrenBy={childrenBy} on={on} members={members} />)
          )}
        </div>
      ) : topKids.length > 0 ? (
        <div className="border-t border-line px-2.5 py-1.5">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">{topKids.length} subtask{topKids.length === 1 ? '' : 's'}</div>
          {topKids.map((c) => <MiniRow key={c.id} item={c} statuses={statusesByBoard.get(c.board_id)} depth={0} onOpen={openInNewTab} childrenBy={childrenBy} on={on} members={members} />)}
        </div>
      ) : null}
    </div>
  );
}
